/**
 * Pending-approvals summary — the Inbox surface tab's live count
 * (docs/58; backend GET /api/v1/approvals/summary).
 *
 * One cheap counts-only read instead of the 9 per-module list queries the
 * /approvals page itself uses. Every type's count is role-scoped in SQL to
 * the caller (a non-approver gets zeros), so the badge is honest by
 * construction — it never shows work the caller can't act on.
 *
 * staleTime 60s + refetchOnWindowFocus keeps the badge fresh without
 * polling chatter; every approval decision mutation invalidates
 * APPROVALS_SUMMARY_QUERY_KEY (lib/approvals-invalidate.ts) for an
 * immediate update.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { APPROVALS_SUMMARY_QUERY_KEY } from "@/lib/approvals-invalidate";

export {
  APPROVALS_SUMMARY_QUERY_KEY,
  invalidateApprovalsSummary,
} from "@/lib/approvals-invalidate";

export interface ApprovalsSummary {
  pending: number;
  by_type: Record<string, number>;
}

/** Shared fetcher so route loaders can prefetch under the same key. */
export async function fetchApprovalsSummary(
  signal?: AbortSignal,
): Promise<ApprovalsSummary> {
  const { data } = await api.get<ApprovalsSummary>(
    "/api/v1/approvals/summary",
    { signal },
  );
  return data;
}

export function useApprovalsSummary(enabled = true) {
  return useQuery({
    queryKey: APPROVALS_SUMMARY_QUERY_KEY,
    queryFn: ({ signal }) => fetchApprovalsSummary(signal),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}
